/*
 * division-node.ts - a spatial division node of the reference frame
 */

import type LegacyMap from './legacy-map';
import type MapSrs from './srs';


/**
 * One node of the reference frame's spatial division: the SRS the tiles
 * under it are addressed in, its extents in that SRS, and the
 * conversions between that SRS, the navigation SRS and the physical
 * SRS.
 *
 * The node also answers the scale factor of its projection at a
 * position, `scaleFactor`, which is what relates a resolution in metres
 * to the nominal spacing of its tiles.
 */
class MapDivisionNode {

    constructor(
        map: LegacyMap,
        id: number[],
        srs: string,
        extents: MapDivisionNode.Extents,
        heightRange: [number, number],
        partitioning: MapDivisionNode.Partitioning,
    ) {

        this.map = map;
        this.id = id;
        this.srs = map.getMapsSrs(srs);
        this.extents = extents;
        this.heightRange = heightRange;
        this.partitioning = partitioning;
        this.isPole = id[0] === 1
            && ((id[1] === 0 && id[2] === 1) || (id[1] === 1 && id[2] === 0));
    }

    getInnerCoords(coords: number[]): number[] {

        return this.srs.convertCoordsFrom(coords, this.map.getNavigationSrs());
    }

    getOuterCoords(coords: number[]): number[] {

        return this.srs.convertCoordsTo(coords, this.map.getNavigationSrs());
    }

    getPhysicalCoords(
        coords: number[], skipVerticalAdjust?: boolean,
    ): number[] {

        return this.srs.convertCoordsTo(
            coords, this.map.getPhysicalSrs(), skipVerticalAdjust);
    }

    getPhysicalCoordsFast(
        coords: ArrayLike<number>,
        skipVerticalAdjust: boolean,
        coords2: ArrayLike<number>,
        index: number,
        index2: number,
    ): void {

        this.srs.convertCoordsToFast(
            coords, this.map.getPhysicalSrs(), skipVerticalAdjust,
            coords2, index, index2);
    }

    getExtents(coords: number[]): number[] {

        return this.srs.convertCoordsFrom(coords, this.map.getNavigationSrs());
    }

    /**
     * The scale factor of the node's projection at the given node
     * coordinates: units of the node's SRS per metre on the ground, the
     * geometric mean over the two axes so that one figure serves a
     * square cell of the node's tiles. Read by bilinear interpolation
     * from the scale grid, which covers the node's extents and is built
     * on first use.
     */
    scaleFactor(coords: readonly number[]): number {

        const grid = this.scaleGrid_ ??= this.buildScaleGrid();
        const size = ScaleGridSize;
        const ll = this.extents.ll;
        const ur = this.extents.ur;

        const fx = (coords[0] - ll[0]) / (ur[0] - ll[0]) * (size - 1);
        const fy = (coords[1] - ll[1]) / (ur[1] - ll[1]) * (size - 1);

        const ix = Math.min(Math.max(Math.floor(fx), 0), size - 2);
        const iy = Math.min(Math.max(Math.floor(fy), 0), size - 2);

        const tx = Math.min(Math.max(fx - ix, 0), 1);
        const ty = Math.min(Math.max(fy - iy, 0), 1);

        const v00 = grid[iy * size + ix];
        const v10 = grid[iy * size + ix + 1];
        const v01 = grid[(iy + 1) * size + ix];
        const v11 = grid[(iy + 1) * size + ix + 1];

        return (v00 * (1 - tx) + v10 * tx) * (1 - ty)
            + (v01 * (1 - tx) + v11 * tx) * ty;
    }

    /**
     * Builds the scale grid: `ScaleGridSize` by `ScaleGridSize` values of
     * `scaleFactorAt` spaced evenly over the node's extents, the boundary
     * rows and columns included.
     */
    private buildScaleGrid(): Float64Array {

        const size = ScaleGridSize;
        const ll = this.extents.ll;
        const ur = this.extents.ur;
        const width = ur[0] - ll[0];
        const height = ur[1] - ll[1];

        // the finite-difference step: one metre in the SRS's own units,
        // kept well inside the node
        const unitMetres = (this.srs.getSrsInfo()['unitMetres'] as number) || 1;
        const stepX = Math.min(1 / unitMetres, width / 4);
        const stepY = Math.min(1 / unitMetres, height / 4);

        const grid = new Float64Array(size * size);

        for (let j = 0; j < size; j++) {

            const y = ll[1] + height * (j / (size - 1));

            for (let i = 0; i < size; i++) {

                const x = ll[0] + width * (i / (size - 1));

                // a boundary cell steps inward: beyond the node's edge
                // the projection may describe another face of the body
                const value = this.scaleFactorAt(
                    x, y,
                    i === size - 1 ? -stepX : stepX,
                    j === size - 1 ? -stepY : stepY,
                    i > 0 && i < size - 1,
                    j > 0 && j < size - 1);

                if (__DEV__ && !(value > 0))
                    throw new Error('division node: projection failed '
                        + 'inside the node\'s extents');

                grid[j * size + i] = value;
            }
        }

        return grid;
    }

    /**
     * The scale factor at `(x, y)`, from the ground area of a unit square
     * of the node's SRS there: the length of the cross product of the two
     * partial derivatives of the conversion into the physical SRS, taken
     * by centred differences where `centred` allows and one-sided ones
     * at the node's boundary. The scale factor is the reciprocal of that
     * area's square root.
     */
    private scaleFactorAt(
        x: number,
        y: number,
        stepX: number,
        stepY: number,
        centredX: boolean,
        centredY: boolean,
    ): number {

        const at = (px: number, py: number): number[] =>
            this.getPhysicalCoords([px, py, 0], true);

        const derivative = (
            dx: number, dy: number, step: number, centred: boolean,
        ): number[] => {

            const forward = at(x + dx, y + dy);
            const back = centred ? at(x - dx, y - dy) : at(x, y);
            const span = centred ? 2 * step : step;

            return [
                (forward[0] - back[0]) / span,
                (forward[1] - back[1]) / span,
                (forward[2] - back[2]) / span,
            ];
        };

        const du = derivative(stepX, 0, stepX, centredX);
        const dv = derivative(0, stepY, stepY, centredY);

        const cross = [
            du[1] * dv[2] - du[2] * dv[1],
            du[2] * dv[0] - du[0] * dv[2],
            du[0] * dv[1] - du[1] * dv[0],
        ];

        return 1 / Math.sqrt(Math.hypot(cross[0], cross[1], cross[2]));
    }

    readonly map: LegacyMap;
    readonly id: number[];
    readonly srs: MapSrs;
    readonly extents: MapDivisionNode.Extents;
    readonly heightRange: [number, number];
    readonly partitioning: MapDivisionNode.Partitioning;
    readonly isPole: boolean;

    /** The scale grid, built on first use. */
    private scaleGrid_: Float64Array | null = null;
}


/**
 * Cells per side of the scale grid. Linear interpolation on 17 cells
 * stays within two percent of the exact scale factor on a global
 * Mercator node, whose factor spans an order of magnitude from the
 * equator to its edge; the other projections in use vary far less.
 */
const ScaleGridSize = 17;


namespace MapDivisionNode {

    /** The node's extents in its own SRS. */
    export type Extents = {
        ll: [number, number];
        ur: [number, number];
    };

    /**
     * How the node partitions its tiles among child nodes: a keyword such
     * as `none` or `barren`, a table of ranges keyed by child position,
     * or nothing.
     */
    export type Partitioning = string | Record<string, unknown> | null
        | undefined;
}


export default MapDivisionNode;
